import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiOutlineShieldCheck } from "react-icons/hi";
import Button from "../shared/Button";
import { getTwoFactorStatus } from "../../redux/api/authAPI";

const SECURITY_PATH = "/account/security";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const snoozeKey = (userId) => `twoFactorPromptSnoozedUntil:${userId}`;

const isSnoozed = (userId) => {
  try {
    return Number(localStorage.getItem(snoozeKey(userId))) > Date.now();
  } catch {
    return false;
  }
};

/**
 * Banner asking users without 2FA to turn it on.
 * "Remind me later" hides it for 7 days in this browser.
 */
const TwoFactorPrompt = ({ userId }) => {
  const onSecurityPage = useLocation().pathname === SECURITY_PATH;
  const [show, setShow] = useState(false);

  // Re-checked whenever the user leaves the security page, so the banner
  // is gone once they have enabled 2FA there.
  useEffect(() => {
    if (onSecurityPage || isSnoozed(userId)) {
      setShow(false);
      return;
    }
    let cancelled = false;
    getTwoFactorStatus().then(({ data }) => {
      if (!cancelled) setShow(Boolean(data) && !data.enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [onSecurityPage, userId]);

  const remindLater = () => {
    try {
      localStorage.setItem(snoozeKey(userId), String(Date.now() + SNOOZE_MS));
    } catch {
      // Storage unavailable: hide for this session only.
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="mt-6 flex flex-col gap-4 rounded-lg border border-hairline bg-canvas p-4 sm:flex-row sm:items-center"
      data-help-title="Two-factor reminder"
      data-help="Shown while two-factor authentication is off for your account. It disappears once you turn 2FA on, or for 7 days if you choose Remind me later."
    >
      <HiOutlineShieldCheck className="h-6 w-6 shrink-0 text-ink" />
      <div className="flex-1">
        <p className="text-body-md font-medium text-ink">
          Protect your account with two-factor authentication
        </p>
        <p className="text-body-md text-muted">
          Add a code from your phone at sign-in so a stolen password is not
          enough to get in. It takes about a minute.
        </p>
      </div>
      <div className="flex gap-3">
        <Button
          as={Link}
          to={SECURITY_PATH}
          variant="primary"
          size="sm"
          data-help-title="Enable 2FA"
          data-help="Opens Account security, where you can set up two-factor authentication with an authenticator app."
        >
          Enable 2FA
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={remindLater}
          data-help-title="Remind me later"
          data-help="Hides this reminder for 7 days in this browser."
        >
          Remind me later
        </Button>
      </div>
    </div>
  );
};

export default TwoFactorPrompt;
